import { useState, useCallback, useMemo, useRef } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useNavigate } from "react-router-dom";
import { ProColumns } from "@ant-design/pro-components";
import { Edit, Search } from "lucide-react";
import Layout from "../Layout";
import {
  Table as UiTable,
  TableBody as UiTableBody,
  TableCell as UiTableCell,
  TableHead as UiTableHead,
  TableHeader as UiTableHeader,
  TableRow as UiTableRow,
} from "../ui/table";
import navigationRoutes from "../../utils/navigationRoutes";
import DynamicModuleService from "@/services/APIs/DynamicModuleService";
import { SchemaService } from "@/services/APIs";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "../ui/badge";
import { formatDate } from "../../utils/dateFormatter";

const PP_MaxBidCalculation: React.FC = () => {
  const navigate = useNavigate();

  const schemaName = "Property Master";
  const maxBidSchemaName = "Max Bid";

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeSearchTerm, setActiveSearchTerm] = useState<string>("");
  const loggedProperties = useRef<Set<string>>(new Set());
  const statusLoggedRef = useRef(false);

  // Get Max Bid schema columns
  const { data: maxBidColumnData } = useQuery({
    queryKey: ["columnGridData", maxBidSchemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(maxBidSchemaName);
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!maxBidSchemaName,
  });

  // Fetch all Max Bid records
  const { data: allMaxBidData } = useQuery({
    queryKey: ["all-max-bid-data", maxBidSchemaName, maxBidColumnData],
    queryFn: async () => {
      if (!maxBidColumnData?.grid) {
        console.log("No maxBidColumnData grid available");
        return { results: [], count: 0 };
      }

      const allColumns = maxBidColumnData.grid.map(
        (field: any) => field.field_name
      );
      const lowerColumns = allColumns.map((col: string) => col.toLowerCase());
      if (!lowerColumns.includes("id")) {
        allColumns.push("id");
      }
      if (!lowerColumns.includes("propertymaster_id")) {
        allColumns.push("propertymaster_id");
      }

      console.log("Fetching Max Bid data with columns:", allColumns);

      try {
        const response = await DynamicModuleService.getGridCoByConfig(
          maxBidSchemaName,
          1,
          10000,
          allColumns,
          undefined
        );

        console.log("Max Bid API response:", response);
        console.log("Response results:", response?.results);
        console.log("Response data:", response?.data);
        console.log("Response count:", response?.count);

        // Try different response structures
        let results: any[] = [];
        const responseAny = response as any;
        if (Array.isArray(response)) {
          results = response;
        } else if (responseAny?.results && Array.isArray(responseAny.results)) {
          results = responseAny.results;
        } else if (responseAny?.data && Array.isArray(responseAny.data)) {
          results = responseAny.data;
        }

        const count = responseAny?.count ?? results.length;

        console.log("Processed results:", results);
        console.log("Processed count:", count);

        return { results, count };
      } catch (error) {
        console.error("Error fetching all max bid data:", error);
        return { results: [], count: 0 };
      }
    },
    enabled: !!maxBidSchemaName && !!maxBidColumnData,
  });

  // Create a map of propertymaster_id to Max Bid data
  const maxBidDataMap = useMemo(() => {
    const map = new Map<string, any>();
    if (allMaxBidData) {
      const allMaxBidDataAny = allMaxBidData as any;
      const data = allMaxBidData.results ?? allMaxBidDataAny.data ?? [];
      if (Array.isArray(data)) {
        console.log("Creating Max Bid map from", data.length, "records");
        data.forEach((record: any) => {
          const propertyMasterIdKey = Object.keys(record).find(
            key => key.toLowerCase() === "propertymaster_id"
          );
          if (propertyMasterIdKey && record[propertyMasterIdKey]) {
            const propId = String(record[propertyMasterIdKey]);
            map.set(propId, record);
            // Log first record details
            if (map.size === 1) {
              console.log("First Max Bid record:", {
                propertymaster_id: propId,
                allKeys: Object.keys(record),
                lienamount:
                  record.lienamount ??
                  record.Lienamount ??
                  record.LienAmount ??
                  "NOT FOUND",
                cash4keys:
                  record.cash4keys ??
                  record.Cash4keys ??
                  record.Cash4Keys ??
                  "NOT FOUND",
              });
            }
          }
        });
        console.log("Max Bid map created with", map.size, "entries");
        // Log all property IDs in map
        if (map.size > 0) {
          console.log(
            "Property IDs in map:",
            Array.from(map.keys()).slice(0, 5)
          );
        }
      }
    } else {
      console.log("No Max Bid data available");
    }
    return map;
  }, [allMaxBidData]);

  // const handleDelete = useCallback(async (id: string) => {
  //   try {
  //     await DynamicModuleService.deleteItem(schemaName, id);
  //     notify.success("Property deleted successfully!");
  //     queryClient.invalidateQueries({
  //       queryKey: ["moduleData"],
  //       exact: false
  //     });
  //   } catch (error) {
  //     console.error("Delete error:", error);
  //     notify.error("Failed to delete property");
  //   }
  // }, [schemaName, queryClient]);

  const handleEdit = (id: string) => {
    navigate(
      "/property-portal" +
        navigationRoutes.EDIT_MAX_BID_CALCULATION.replace(":id", id)
    );
  };

  const handleExportToExcel = useCallback(async () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      toast.error("Please select at least one property to export");
      return;
    }

    try {
      toast.loading("Exporting properties to Excel...", { id: "export-excel" });

      const maxBidSchemaName = "Max Bid";
      const rehabEstimateSchemaName = "Rehab Estimate";

      // Get Max Bid and Rehab Estimate schema columns
      const [maxBidColumnData, rehabEstimateColumnData] = await Promise.all([
        SchemaService.getColumnGridData(maxBidSchemaName),
        SchemaService.getColumnGridData(rehabEstimateSchemaName),
      ]);

      const allMaxBidColumns =
        maxBidColumnData?.grid?.map((field: any) => field.field_name) ?? [];
      if (
        !allMaxBidColumns.map((col: string) => col.toLowerCase()).includes("id")
      ) {
        allMaxBidColumns.push("id");
      }

      const allRehabColumns =
        rehabEstimateColumnData?.grid?.map((field: any) => field.field_name) ??
        [];
      if (
        !allRehabColumns.map((col: string) => col.toLowerCase()).includes("id")
      ) {
        allRehabColumns.push("id");
      }

      // Fetch all selected properties by their IDs
      const propertiesResponses = await Promise.all(
        selectedRowKeys.map(id =>
          DynamicModuleService.getItemById(schemaName, String(id))
        )
      );

      // Extract property data from responses (handle different response structures)
      const propertiesData = propertiesResponses.map((response: any) => {
        // Handle different response structures
        return response?.data ?? response ?? {};
      });

      // Fetch Max Bid and Rehab Estimate data for each property
      const maxBidDataMap = new Map<string, any>();
      const rehabEstimateDataMap = new Map<string, any>();

      await Promise.all(
        selectedRowKeys.map(async propertyId => {
          try {
            // Fetch Max Bid data
            const maxBidResponse = await DynamicModuleService.getGridCoByConfig(
              maxBidSchemaName,
              1,
              1000,
              allMaxBidColumns,
              {
                propertymaster_id: String(propertyId),
              }
            );

            const maxBidData =
              maxBidResponse?.results?.[0] ??
              maxBidResponse?.data?.[0] ??
              maxBidResponse?.[0] ??
              null;
            if (maxBidData) {
              maxBidDataMap.set(String(propertyId), maxBidData);
            }

            // Fetch Rehab Estimate data
            const rehabResponse = await DynamicModuleService.getGridCoByConfig(
              rehabEstimateSchemaName,
              1,
              1000,
              allRehabColumns,
              {
                propertymaster_id: String(propertyId),
              }
            );

            const rehabData =
              rehabResponse?.results?.[0] ??
              rehabResponse?.data?.[0] ??
              rehabResponse?.[0] ??
              null;
            if (rehabData) {
              rehabEstimateDataMap.set(String(propertyId), rehabData);
            }
          } catch (error) {
            console.error(
              `Error fetching related data for property ${propertyId}:`,
              error
            );
          }
        })
      );

      // Define the columns to export with their display names
      const exportColumns = [
        { key: "propertyname", label: "Property name", source: "property" },
        { key: "finalarv", label: "Final ARV", source: "property" },
        { key: "lienamount", label: "Lien Amount", source: "property" },
        { key: "cash4keys", label: "Cash4Keys", source: "maxbid" },
        { key: "tts", label: "TTS", source: "maxbid" },
        { key: "maxbid", label: "Max Bid", source: "maxbid" },
        { key: "rehabestimate", label: "Rehab Estimate", source: "rehab" },
      ];

      // Prepare data for Excel
      const excelData = propertiesData.map((property: any) => {
        const propertyId = property.id ?? property.ID ?? "";
        const maxBidData = maxBidDataMap.get(String(propertyId)) ?? {};
        const rehabData = rehabEstimateDataMap.get(String(propertyId)) ?? {};

        const row: any = {};
        exportColumns.forEach(col => {
          let value: any = "";

          // Get value from appropriate source
          let sourceData: any = {};
          if (col.source === "property") {
            sourceData = property;
          } else if (col.source === "maxbid") {
            sourceData = maxBidData;
          } else if (col.source === "rehab") {
            sourceData = rehabData;
          }

          // Find the field key (case-insensitive search)
          const fieldKey = Object.keys(sourceData ?? {}).find(
            key => key.toLowerCase() === col.key.toLowerCase()
          );

          if (fieldKey) {
            value = sourceData[fieldKey];
          }

          // Handle array/object values
          if (Array.isArray(value)) {
            value = value
              .map((item: any) => {
                if (typeof item === "object" && item !== null) {
                  return (
                    item.url ?? item.name ?? item.id ?? JSON.stringify(item)
                  );
                }
                return item;
              })
              .join(", ");
          } else if (typeof value === "object" && value !== null) {
            value =
              value.url ?? value.name ?? value.id ?? JSON.stringify(value);
          }

          row[col.label] = value ?? "";
        });
        return row;
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Max Bid Calculation");

      // Generate Excel file and download
      const fileName = `Max_Bid_Calculation_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success(
        `Successfully exported ${selectedRowKeys.length} property(ies) to Excel`,
        { id: "export-excel" }
      );
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error(
        error?.response?.data?.message ??
          "Failed to export properties to Excel",
        { id: "export-excel" }
      );
    }
  }, [selectedRowKeys, schemaName]);

  const columns: ProColumns<any>[] = [
    {
      title: "Date",
      dataIndex: "date",
      render: (_, record) => formatDate(record.date),
    },
    {
      title: "Property Name",
      dataIndex: "propertyname",
    },
    {
      title: "Lien Amount",
      dataIndex: "lienamount",
    },
    {
      title: "Sq. Footage",
      dataIndex: "sqfootage",
    },
    {
      title: "Status",
      width: 120,
      render: (_, record) => {
        const propertyId = record.id ?? record.ID ?? record.Id;
        if (!propertyId) {
          console.log("No property ID found in record:", record);
          return (
            <Badge
              style={{
                backgroundColor: "rgb(254, 249, 195)",
                color: "rgb(133, 77, 14)",
                border: "none",
              }}
            >
              Pending
            </Badge>
          );
        }

        const propertyIdStr = String(propertyId);
        const maxBidData = maxBidDataMap.get(propertyIdStr);

        if (!maxBidData) {
          // Only log once per property to avoid spam
          if (
            maxBidDataMap.size > 0 &&
            !loggedProperties.current.has(propertyIdStr)
          ) {
            console.log(
              `No Max Bid data for property ${propertyIdStr}. Map has ${maxBidDataMap.size} entries. Available IDs:`,
              Array.from(maxBidDataMap.keys()).slice(0, 3)
            );
            loggedProperties.current.add(propertyIdStr);
          }
          return (
            <Badge
              style={{
                backgroundColor: "rgb(254, 249, 195)",
                color: "rgb(133, 77, 14)",
                border: "none",
              }}
            >
              Pending
            </Badge>
          );
        }

        // Helper function to find field value (case-insensitive)
        const getFieldValue = (fieldName: string) => {
          if (maxBidData[fieldName] !== undefined) {
            return maxBidData[fieldName];
          }
          const key = Object.keys(maxBidData).find(
            k => k.toLowerCase() === fieldName.toLowerCase()
          );
          return key ? maxBidData[key] : undefined;
        };

        // Check if lienamount and cash4keys are populated
        const lienamount = getFieldValue("lienamount");
        const cash4keys = getFieldValue("cash4keys");

        // Check if fields are blank (null, undefined, empty string, or whitespace)
        const isLiensBlank =
          lienamount === undefined ||
          lienamount === null ||
          lienamount === "" ||
          (typeof lienamount === "string" && lienamount.trim() === "");
        const isCash4KeysBlank =
          cash4keys === undefined ||
          cash4keys === null ||
          cash4keys === "" ||
          (typeof cash4keys === "string" && cash4keys.trim() === "");

        // If either is blank, status is Pending, otherwise Completed
        const status =
          isLiensBlank || isCash4KeysBlank ? "Pending" : "Completed";

        // Log first status check for debugging
        if (!statusLoggedRef.current) {
          console.log(`Status check for property ${propertyIdStr}:`, {
            lienamount,
            cash4keys,
            isLiensBlank,
            isCash4KeysBlank,
            status,
            allKeys: Object.keys(maxBidData),
          });
          statusLoggedRef.current = true;
        }

        return (
          <Badge
            style={
              status === "Completed"
                ? {
                    backgroundColor: "rgb(220, 252, 231)",
                    color: "rgb(22, 101, 52)",
                    border: "none",
                  }
                : {
                    backgroundColor: "rgb(254, 249, 195)",
                    color: "rgb(133, 77, 14)",
                    border: "none",
                  }
            }
          >
            {status}
          </Badge>
        );
      },
    },
    {
      title: "ID",
      dataIndex: "id",
      hideInTable: true,
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <Button
          key="edit"
          variant="ghost"
          size="icon"
          onClick={() => handleEdit(record.id)}
        >
          <Edit size={16} />
        </Button>,
        // <Popconfirm
        //   key="delete"
        //   title="Are you sure you want to delete this property?"
        //   onConfirm={() => handleDelete(record.id)}
        //   okText="Yes"
        //   cancelText="No"
        // >
        //   <Button variant="ghost" size="icon">
        //     <Trash size={16} />
        //   </Button>
        // </Popconfirm>,
      ],
    },
  ];

  console.log("Columns defined:", columns);
  console.log(
    "DataIndex values being sent to API:",
    columns.filter(col => col.dataIndex).map(col => col.dataIndex)
  );

  const handleSearch = useCallback(() => {
    setActiveSearchTerm(searchTerm.trim());
  }, [searchTerm]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setActiveSearchTerm("");
  }, []);

  // Fetch module data for table (same as ConfigDynamicTable would do)
  const { data: moduleData, isLoading: moduleLoading } = useQuery({
    queryKey: ["moduleData", schemaName, activeSearchTerm],
    queryFn: async () => {
      try {
        const columnsToFetch = columns
          .filter(col => (col as any).dataIndex)
          .map(col => (col as any).dataIndex as string);
        if (!columnsToFetch.map(c => c.toLowerCase()).includes("id")) {
          columnsToFetch.push("id");
        }
        const resp = await DynamicModuleService.getGridCoByConfig(
          schemaName,
          1,
          1000,
          columnsToFetch,
          {}
        );
        return resp ?? { results: [], count: 0 };
      } catch (err) {
        console.error("Error fetching module data:", err);
        return { results: [], count: 0 };
      }
    },
    enabled: !!schemaName,
  });

  const rawTableData = useMemo(() => {
    if (!moduleData) {
      return [];
    }
    return Array.isArray(moduleData) ? moduleData : (moduleData.results ?? []);
  }, [moduleData]);

  const filteredTableData = useMemo(() => {
    if (!activeSearchTerm) {
      return rawTableData;
    }
    const term = activeSearchTerm.toLowerCase();
    return rawTableData.filter((item: any) => {
      const propertyName = item.propertyname ?? item.propertyName ?? "";
      return String(propertyName).toLowerCase().includes(term);
    });
  }, [rawTableData, activeSearchTerm]);

  const getIdFromRecord = (rec: any) =>
    rec.id ??
    rec.ID ??
    rec.Id ??
    rec._id ??
    (Object.keys(rec ?? {}).find(k => k.toLowerCase() === "id")
      ? rec[Object.keys(rec).find(k => k.toLowerCase() === "id") as string]
      : undefined);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredTableData
        .map((r: any) => String(getIdFromRecord(r)))
        .filter(Boolean);
      setSelectedRowKeys(allIds);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    const idStr = String(id);
    if (checked) {
      setSelectedRowKeys(prev => Array.from(new Set([...(prev ?? []), idStr])));
    } else {
      setSelectedRowKeys(prev => (prev ?? []).filter(k => String(k) !== idStr));
    }
  };

  return (
    <Layout>
      <div className="space-y-4 lg:space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-foreground lg:text-3xl">
            Max Bid Calculation
          </h1>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-[#3d3d7a] bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90"
              onClick={handleExportToExcel}
              disabled={!selectedRowKeys || selectedRowKeys.length === 0}
            >
              Export Excel
            </Button>
            {/* <Button
              variant="outline"
              className="bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90 border-[#3d3d7a]"
            >
              Upload
            </Button>
            <Button
              className="bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90"
              onClick={() => navigate("/property-portal/create-property")}
            >
              + Add Property
            </Button> */}
          </div>
        </div>

        {/* Search Box */}
        <div className="flex items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by property name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              className="pl-10 pr-10"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                type="button"
              >
                ×
              </button>
            )}
          </div>
          <Button
            type="button"
            onClick={handleSearch}
            className="bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90"
            disabled={!searchTerm.trim()}
          >
            <Search className="mr-2 h-4 w-4" />
            Search
          </Button>
          {activeSearchTerm && (
            <Button variant="outline" onClick={handleClearSearch}>
              Clear
            </Button>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <UiTable>
              <UiTableHeader>
                <UiTableRow className="bg-muted/50">
                  <UiTableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={
                        filteredTableData.length > 0 &&
                        selectedRowKeys.length > 0 &&
                        selectedRowKeys.length ===
                          filteredTableData
                            .map((r: any) => String(getIdFromRecord(r)))
                            .filter(Boolean).length
                      }
                      onChange={e => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-[#4a5273] focus:ring-[#4a5273]"
                    />
                  </UiTableHead>
                  <UiTableHead>NO.</UiTableHead>
                  <UiTableHead>Date</UiTableHead>
                  <UiTableHead>Property Name</UiTableHead>
                  <UiTableHead>Lien Amount</UiTableHead>
                  <UiTableHead>Sq. Footage</UiTableHead>
                  <UiTableHead>Status</UiTableHead>
                  <UiTableHead>ACTION</UiTableHead>
                </UiTableRow>
              </UiTableHeader>
              <UiTableBody>
                {moduleLoading ? (
                  <UiTableRow>
                    <UiTableCell colSpan={8} className="py-8 text-center">
                      Loading...
                    </UiTableCell>
                  </UiTableRow>
                ) : filteredTableData.length === 0 ? (
                  <UiTableRow>
                    <UiTableCell colSpan={8} className="py-8 text-center">
                      No properties found
                    </UiTableCell>
                  </UiTableRow>
                ) : (
                  filteredTableData.map((record: any, idx: number) => {
                    const id = String(getIdFromRecord(record) ?? idx);
                    const dateVal = formatDate(
                      record.date ?? record.Date ?? record.created_at
                    );
                    const propertyName =
                      record.propertyname ?? record.propertyName ?? "-";
                    const lienAmount =
                      record.lienamount ?? record.lienAmount ?? "-";
                    const sq = record.sqfootage ?? record.sqFootage ?? "-";

                    const propertyIdStr = String(id);
                    const maxBidData = maxBidDataMap.get(propertyIdStr);

                    // Helper function to find field value (case-insensitive)
                    const getFieldValue = (fieldName: string) => {
                      if (!maxBidData) {
                        return undefined;
                      }
                      if (maxBidData[fieldName] !== undefined) {
                        return maxBidData[fieldName];
                      }
                      const key = Object.keys(maxBidData).find(
                        k => k.toLowerCase() === fieldName.toLowerCase()
                      );
                      return key ? maxBidData[key] : undefined;
                    };

                    // Check if lienamount and cash4keys are populated
                    const lienamount = getFieldValue("lienamount");
                    const cash4keys = getFieldValue("cash4keys");

                    // Check if fields are blank (null, undefined, empty string, or whitespace)
                    const isLiensBlank =
                      lienamount === undefined ||
                      lienamount === null ||
                      lienamount === "" ||
                      (typeof lienamount === "string" &&
                        lienamount.trim() === "");
                    const isCash4KeysBlank =
                      cash4keys === undefined ||
                      cash4keys === null ||
                      cash4keys === "" ||
                      (typeof cash4keys === "string" &&
                        cash4keys.trim() === "");

                    // If either is blank, status is Pending, otherwise Completed
                    const status =
                      !maxBidData || isLiensBlank || isCash4KeysBlank
                        ? "Pending"
                        : "Completed";

                    const isSelected = (selectedRowKeys ?? []).some(
                      k => String(k) === id
                    );

                    return (
                      <UiTableRow key={id}>
                        <UiTableCell>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={e =>
                              handleSelectRow(id, e.target.checked)
                            }
                            className="h-4 w-4 rounded border-gray-300 text-[#4a5273] focus:ring-[#4a5273]"
                          />
                        </UiTableCell>
                        <UiTableCell>{idx + 1}</UiTableCell>
                        <UiTableCell>{dateVal}</UiTableCell>
                        <UiTableCell className="font-medium">
                          {propertyName}
                        </UiTableCell>
                        <UiTableCell>{lienAmount}</UiTableCell>
                        <UiTableCell>{sq}</UiTableCell>
                        <UiTableCell>
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              status === "Completed"
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {status}
                          </span>
                        </UiTableCell>
                        <UiTableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(id)}
                          >
                            <Edit className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </UiTableCell>
                      </UiTableRow>
                    );
                  })
                )}
              </UiTableBody>
            </UiTable>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PP_MaxBidCalculation;
